import { useId } from 'react';

/**
 * Brand crypto-asset glyph for a ticker. Known assets render their SVG
 * mark; anything else falls back to the first letter on a neutral chip
 * (so a freshly-added asset doesn't crash the avatar). Mirrors the
 * marks used in the in-app PriceToBeat header.
 */
export function CoinIcon({ symbol, size = 28 }: { symbol: string; size?: number }) {
  if (symbol === 'BTC') return <BtcIcon size={size} />;
  if (symbol === 'ETH') return <EthIcon size={size} />;
  if (symbol === 'SOL') return <SolIcon size={size} />;
  return (
    <span
      aria-hidden
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-pb-paper2 font-bold text-tg-text"
    >
      {(symbol[0] ?? '?').toUpperCase()}
    </span>
  );
}

function BtcIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <circle cx="16" cy="16" r="16" fill="#F7931A" />
      <path
        fill="#FFFFFF"
        d="M21.6 14.2c.3-1.9-1.2-3-3.2-3.6l.6-2.6-1.6-.4-.6 2.5-1.3-.3.6-2.5-1.6-.4-.7 2.6-1-.3-2.2-.5-.4 1.7s1.2.3 1.2.3c.6.2.7.6.7.9l-1.7 6.8c-.1.2-.3.4-.6.3 0 0-1.2-.3-1.2-.3l-.8 1.8 2.1.5 1.1.3-.7 2.6 1.6.4.6-2.5 1.3.3-.6 2.5 1.6.4.7-2.6c2.7.5 4.8.3 5.6-2.1.7-2 0-3.1-1.4-3.8 1-.2 1.8-.9 2-2.3zM18.4 18.6c-.5 2-3.7.9-4.8.6l.9-3.4c1 .3 4.4.8 3.9 2.8zm.5-4.5c-.4 1.8-3.1.9-4 .6l.8-3.1c.9.2 3.6.7 3.2 2.5z"
      />
    </svg>
  );
}

function EthIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <g fill="#FFFFFF" fillRule="nonzero">
        <path opacity="0.6" d="M16.5 4v8.87L24 16.22z" />
        <path d="M16.5 4 9 16.22l7.5-3.35z" />
        <path opacity="0.6" d="M16.5 21.97V28L24 17.62z" />
        <path d="M16.5 28v-6.04L9 17.62z" />
        <path opacity="0.2" d="m16.5 20.57 7.5-4.35-7.5-3.35z" />
        <path opacity="0.6" d="m9 16.22 7.5 4.35v-7.7z" />
      </g>
    </svg>
  );
}

function SolIcon({ size }: { size: number }) {
  const gradId = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden className="shrink-0">
      <defs>
        <linearGradient id={gradId} x1="4" y1="28" x2="28" y2="4" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#9945FF" />
          <stop offset="0.5" stopColor="#7C3AED" />
          <stop offset="1" stopColor="#14F195" />
        </linearGradient>
      </defs>
      <circle cx="16" cy="16" r="16" fill="#0B0F1A" />
      <g fill={`url(#${gradId})`}>
        <path d="M9.4 21.6h14.2c.3 0 .5.3.3.6l-2.1 2.4c-.1.2-.3.2-.5.2H7.1c-.3 0-.5-.3-.3-.6l2.1-2.4c.1-.2.3-.2.5-.2z" />
        <path d="M9.4 7.2h14.2c.3 0 .5.3.3.6l-2.1 2.4c-.1.2-.3.2-.5.2H7.1c-.3 0-.5-.3-.3-.6l2.1-2.4c.1-.2.3-.2.5-.2z" />
        <path d="M22.6 14.4H8.4c-.3 0-.5.3-.3.6l2.1 2.4c.1.2.3.2.5.2h14.2c.3 0 .5-.3.3-.6l-2.1-2.4c-.1-.2-.3-.2-.5-.2z" />
      </g>
    </svg>
  );
}
